import router from "@/router";
import store from "@/store/store";
import { APP_VERSION, UPDATED_FROM_KEY } from "@/utils/appVersion";

/**
 * Yeni sürüm yayınlandığında açık sekmeyi güvenli bir anda yeniler.
 *
 * Service worker (skipWaiting + clientsClaim) yeni sürümü indirip devreye alınca sayfanın kontrolü
 * ona geçer ("controllerchange"), ama ekrandaki kod hâlâ eski sürümdür. Kasa ekranları gün boyu
 * açık kaldığı için yenilemeyi kendimiz yaparız — ödeme sürerken ASLA:
 *  - Kasa ekranında: ödeme oturumu yokken ve (ekran PIN kilidindeyken ya da sepette ürün yokken).
 *  - Diğer sayfalarda: bir sonraki sayfa geçişinde.
 */

const CHECK_INTERVAL_MS = 15000;
const SW_UPDATE_POLL_MS = 10 * 60 * 1000;
const CHUNK_RELOAD_KEY = "brew_chunk_reload_at";

let updateReady = false;
let reloading = false;
let checkTimer = null;

function selfServiceState() {
  return store.state && store.state.selfservice ? store.state.selfservice : null;
}

function isOnSelfServiceScreen() {
  const r = router.currentRoute;
  return !!(r && r.path && r.path.toLowerCase().indexOf("/pages/selfservice") === 0);
}

/** Ödeme ekranı açık ya da POS'a ödeme gönderilmiş/kaydedilmişse yenileme yapılmaz. */
function isPaymentInProgress() {
  const s = selfServiceState();
  const ps = s && s.paymentSessionState;
  return !!(ps && (ps.isActive || ps.isPaymentStarted));
}

function hasCartItems() {
  const s = selfServiceState();
  if (!s || !Array.isArray(s.carts)) return false;
  return s.carts.some((c) => c && Array.isArray(c.cartEntries) && c.cartEntries.length > 0);
}

function isSafeToReloadNow() {
  if (isPaymentInProgress()) return false;
  if (!isOnSelfServiceScreen()) return false; // diğer sayfalar sayfa geçişinde yenilenir
  const s = selfServiceState();
  const locked = !!(s && s.screenLock);
  return locked || !hasCartItems();
}

function reloadForUpdate(reason) {
  if (reloading) return;
  reloading = true;
  try {
    sessionStorage.setItem(
      UPDATED_FROM_KEY,
      JSON.stringify({ from: APP_VERSION, reason, at: new Date().toISOString() })
    );
  } catch (e) {
    // sessionStorage yoksa yalnızca teşhis bilgisi eksik kalır
  }
  window.location.reload();
}

function markUpdateReady() {
  if (updateReady) return;
  updateReady = true;
  checkTimer = setInterval(() => {
    if (isSafeToReloadNow()) {
      clearInterval(checkTimer);
      reloadForUpdate("idle");
    }
  }, CHECK_INTERVAL_MS);
}

/** registerServiceWorker.js "registered" anında çağırır. */
export function onServiceWorkerRegistered(registration) {
  // Kasa sekmesi sayfa değiştirmeden gün boyu açık kaldığı için tarayıcı güncellemeyi kendisi
  // kontrol etmez; periyodik olarak biz soruyoruz.
  setInterval(() => {
    registration.update().catch(() => {});
  }, SW_UPDATE_POLL_MS);
}

if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
  // Sayfa kontrolsüz açıldıysa (ilk ziyaret, site verisi silinmiş) ilk controllerchange yalnızca
  // service worker'ın sayfayı devralmasıdır (clientsClaim) — onu atla. Sonraki her değişim = yeni sürüm.
  let skipNextControllerChange = !navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (skipNextControllerChange) {
      skipNextControllerChange = false;
      return;
    }
    markUpdateReady();
  });
}

router.afterEach(() => {
  if (updateReady && !isPaymentInProgress()) {
    reloadForUpdate("routeChange");
  }
});

// Yayından sonra eski sürümün henüz yüklenmemiş bir sayfa parçası sunucuda artık yoksa sayfa boş kalmasın:
// bir kez yenileyip yeni sürümü yükle (döngüye girmemek için 1 dakikada en fazla bir kez).
router.onError((err) => {
  const msg = (err && err.message) || "";
  if (!/Loading (CSS )?chunk .* failed/i.test(msg)) return;
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch (e) {
    last = 0;
  }
  if (Date.now() - last > 60000 && !isPaymentInProgress()) {
    reloadForUpdate("chunkLoadError");
  }
});
